import { eventClient, events } from '@opus/web.core.lib.event-tracking'
import axios from 'axios'
import { isEmpty, omit } from 'lodash'
import { REFRESH_WORKER_TOKEN, SIGN_OUT_WORKER_MUTATION, VERIFY_EXISTED_EMAIL, apolloClient } from '~/common/apollo'
import { COLECTIONS, EVENTS, PATHS } from '~/common/constants'
import { RECAPTCHA_CLIENT_KEY, captureException } from '~/common/helpers'
import { action, computed, event, observable, persist, store } from '~/common/mobx.decorator'
import { getUserTrackingBasicInfor } from '~/common/tracking/event-client.tracking'
import { careFindJobStore } from '~/features/care-find-job/care-find-job.store'
import { homeStore } from '~/features/home/home.store'
import { profileCompletionStore } from '~/features/profile-completion/profile-completion.store'
import { fireStore, routerStore } from '~/stores'
import { notifyStore } from './notify.store'

const matchAnonymousEvents = async () => {
	// ONLY CALL THIS FUNCTION AFTER setUserId & setUserProperties
	try {
		const anonymousEvents = await window.host.getUserEvents()
		const anonymousId = await window.host.getId()

		if (!anonymousEvents || !anonymousEvents.length) return

		const searchEvents = anonymousEvents
			.filter((event) => event.name === 'ANONYMOUS_SEARCH')
			.map(
				(event) =>
					new events.AnonymousSearchSuccessEvent({
						...event.data,
						metadata: {
							ip: event.ip,
							user_agent: event.userAgent,
							anonymous_id: anonymousId,
						},
					})
			)

		searchEvents.forEach((event) => eventClient.logEvent(event))
		await window.host.archiveUserEvents()
		// IN CASE: You want to clean up all archived events
		// Please call: window.host.cleanUserEvents()
	} catch (error) {
		console.debug({
			error,
		})
	}
}

class ProfileItem {
	@persist @observable id
	@persist @observable firstName
	@persist @observable lastName
	@persist @observable workAuthorizedAt
	@persist @observable email
}

const DEFAULT_TOKEN_EXPIRE_IN = 30 * 60 * 1000 // calculate in miliseconds

@store({ persist: true })
class AuthStoreCandidate {
	@persist @observable token = ''
	@persist @observable refreshToken = ''
	@persist @observable maintenanceMessage = ''
	@persist @observable expiresAt = 0
	@persist @observable expiresIn = 0
	@persist @observable tokenType = ''
	@persist('object', ProfileItem) @observable profile
	@persist @observable isExistAccount = false
	@persist @observable notificationChannel = ''
	@persist @observable requireCompleteProfile = false

	@observable currentJob = null
	/** @deprecated the variable will be remove soon */
	@observable isAnonymousLogin = false
	@observable showDialogOtp = false
	@observable phoneNumber = ''
	@observable showChangePasswordDialog = false
	@observable phoneVerifyAt = null
	@observable showResetPassLockAccount = false
	@observable showResetPassLockContent = {}
	@observable ready = false
	@observable worker = {}

	@computed
	get recruiter() {
		return this.profile?.recruiter
	}

	@computed
	get authorization() {
		return !this.token || Date.now() > this.expiresAt ? '' : this.token
	}

	@computed
	get features() {
		return [
			'home',
			'completion_homepage',
			'profile',
			'timecards',
			'popular-cities',
			'popular-cities-detail',
			'find-job',
			'filter-job',
			'edit-filter-job',
			'job-results',
			'list-jobs',
			'urgent-needs',
			'trending-jobs',
			'recommended-jobs',
			'discover',
			'my-jobs',
			'my-badges',
			'credentials',
			'jobs',
			'referral',
			'change-password',
			'update-phone',
			'settings',
			'last-viewed',
			'contact-us',
			'bookmark',
			'job-alerts',
			'search',
			'notifications',
			'privacy-policy',
			'credential',
			'skill-checklists',
			'available-perdiem-shift',
			'profile-completion',
			'verify-credential',
		]
	}

	@computed
	get id() {
		return this.authorization && this.profile?.id
	}

	@computed
	get isLogin() {
		return this.authorization !== ''
	}

	@computed
	get firstName() {
		return this.profile?.firstName
	}

	@computed
	get workerEmail() {
		return this.profile?.email
	}

	@computed
	get lastName() {
		return this.profile?.lastName
	}

	@computed
	get isExistAccountData() {
		return this.isExistAccount
	}

	@computed
	get fullName() {
		return [this.firstName, this.lastName].join(' ').trim()
	}

	@computed
	get isAnonymous() {
		return this.isAnonymousLogin || this.authorization === ''
	}

	@computed
	get activated() {
		return !this.requireCompleteProfile
	}

	@computed
	get requiredCompleteProfile() {
		return this.requireCompleteProfile || false
	}

	@action
	setWorkAuthorized = (workAuthorizedAt) => {
		this.profile = { ...this.profile, workAuthorizedAt }
	}

	@computed
	get noJobMatches() {
		return this.profile?.noJobMatches
	}

	@computed
	get workerAvatar() {
		return this.profile?.avatar?.thumbnails?.s_100x100
	}

	@computed
	get isGoodFlow() {
		return this.worker?.initialScreeningResult === 'good_flow'
	}

	@computed
	get beside() {
		return this.worker?.beside
	}

	@computed
	get hasCurrentJob() {
		return !isEmpty(this.currentJob)
	}

	@action
	setMaintenanceMessage = (message) => {
		this.maintenanceMessage = message
	}

	@action
	changeActiveTabIndex = (tabIndex) => {
		if (this.activeTabIndex === tabIndex) {
			return
		}

		this.prevActiveTabIndex = -1
		this.activeTabIndex = tabIndex
	}

	/** @deprecated the function will be remove soon, use `candidateStore.updateCandidate` instead */
	@action
	changeProfile = async (profile, notificationChannel) => {
		if (this.profile && profile && this.profile?.id === profile?.id && profile.workAuthorized === true && this.activated !== !!profile?.workAuthorizedAt) {
			eventClient.logEvent(new events.OnboardingWorkerAuthorizedSuccessEvent())
		}

		this.profile = profile
		if (notificationChannel) this.notificationChannel = notificationChannel
		if (profile) {
			eventClient.setUserId(profile.id)
			eventClient.setUserProperties({
				id: profile.id,
				company_id: process.env.REACT_APP_COMPANY_ID,
				work_authorized: !!profile.workAuthorizedAt,
			})
			getUserTrackingBasicInfor().then((response) => {
				eventClient.setBasicInfo({
					...response,
					userProperties: {
						id: profile.id,
						company_id: process.env.REACT_APP_COMPANY_ID,
						work_authorized: !!profile.workAuthorizedAt,
					},
				})
			})
			// ONLY CALL THIS FUNCTION AFTER setUserId & setUserProperties
			await matchAnonymousEvents()
		}
		notifyStore.sendInfo()

		// Update LiveChat Info
		if (window.LC_API && typeof window.LC_API.set_visitor_name === 'function') {
			window.LC_API.set_visitor_name([profile?.firstName, profile?.lastName].join(' ').trim())
			window.LC_API.set_visitor_email(profile?.email)
		}
	}

	/**
	 * @deprecated feature will be removed in the future
	 * @param {*} value
	 */
	@action
	handleAuthAnonymous(value) {
		return (this.isAnonymousLogin = value)
	}

	@action
	changeToken = async (authToken) => {
		const { accessToken: token, refreshToken, expiresIn, tokenType } = authToken || {}
		this.tokenType = tokenType
		this.token = token
		this.refreshToken = refreshToken
		this.expiresIn = (expiresIn ?? DEFAULT_TOKEN_EXPIRE_IN) * 1000 - 30 * 1000 // expiresIn unit is seconds
		this.expiresAt = Date.now() + this.expiresIn

		if (token) {
			axios.defaults.headers.common['Authorization'] = `${tokenType === 'bearer' ? 'Bearer' : ''} ${token}`
		} else {
			delete axios.defaults.headers.common['Authorization']
			notifyStore.signal.push(async (OneSignal) => {
				await OneSignal?.User?.PushSubscription?.optOut()
				await OneSignal?.logout()
			})
		}
	}

	@action
	refreshAuthToken = async () => {
		if (this.refreshToken) {
			try {
				const variables = { refreshToken: this.refreshToken }
				const {
					data: {
						refreshToken: { authToken },
					},
				} = await apolloClient.mutate({
					mutation: REFRESH_WORKER_TOKEN,
					variables,
					context: {
						clientName: 'public',
					},
				})

				await this.changeToken(authToken)

				return authToken
			} catch (error) {
				return Promise.resolve({})
			}
		} else {
			return Promise.resolve({})
		}
	}

	@event(EVENTS.authStore.logout)
	async logout(shouldMutateApi = true, redirectLink = PATHS.common.signin) {
		try {
			if (shouldMutateApi) {
				await apolloClient
					.mutate({
						mutation: SIGN_OUT_WORKER_MUTATION,
					})
					.catch((error) => {
						notifyStore.error(error.message)
					})
			}
			await authStore.changeToken({})
			await eventClient.setUserId(null)
			await authStore.handleAuthAnonymous(false)
			authStore.resetPersist()
			careFindJobStore.resetFilterValue()
			await eventClient.setUserProperties(null)
			careFindJobStore.setInitSearchListList()
			notifyStore.unInitOneSignal()
			await routerStore.goPage(redirectLink)
			await getUserTrackingBasicInfor().then((response) => {
				eventClient.setBasicInfo(omit(response, ['userProperties']))
			})

			homeStore.cleanup()
			profileCompletionStore.reset()
			notifyStore.success('You signed out successfully')
		} catch (e) {
			notifyStore.error(e.message)
		}
	}

	@action
	setLastJobId = async (workerId, jobId, jobTitle) => {
		if (!isEmpty(workerId)) {
			const doc = fireStore.db.collection(COLECTIONS.last_job_id).doc(workerId)
			await doc.set({ jobId, jobTitle })
		}
	}

	@action
	handleSetShowDialogOtp = (value) => {
		this.showDialogOtp = value
	}

	@action
	handleSetPhoneNumber = (value) => {
		this.phoneNumber = value
	}

	@action
	setShowChangePasswordDialog = (value) => {
		this.showChangePasswordDialog = value
	}

	@action
	setPhoneVerifyAt = (value) => {
		this.phoneVerifyAt = value
	}
	@action
	setShowResetPassLockAccount = (value) => {
		this.showResetPassLockAccount = value
	}
	@action
	setShowResetPassLockContent = (value) => {
		this.showResetPassLockContent = value
	}
	@action
	resetPersist = () => {
		this.expiresAt = 0
		this.expiresIn = 0
		this.profile = null
		this.isExistAccount = false
		this.notificationChannel = ''
		this.currentJob = null
		this.phoneVerifiedAt = null
		this.requireCompleteProfile = false
	}

	/**
	 * @deprecated will be removed in the future
	 *
	 * @param {*} variables
	 * @param {*} param1
	 * @param {*} history
	 * @returns
	 */
	@action
	verifyExistEmail = async (variables, { setErrors, setSubmitting }, history) => {
		authStore.resetPersist()
		return new Promise((resolve, reject) => {
			window.grecaptcha.ready(async () => {
				try {
					const token = await window.grecaptcha.execute(RECAPTCHA_CLIENT_KEY, {
						action: 'signInWorker',
					})
					let updatedVariables = {
						recaptchaToken: token,
						...variables,
					}
					const {
						data: { verifyExistedEmail },
					} = await apolloClient.mutate({
						mutation: VERIFY_EXISTED_EMAIL,
						variables: updatedVariables,
					})

					const { existed } = verifyExistedEmail
					this.isExistAccount = existed

					if (existed) {
						await history.push(PATHS.common.signin, {
							email: variables.email,
						})
					} else {
						await routerStore.openNewPage(`${process.env.REACT_APP_MARKETING_WEB_SITE}/candidate-registration`)
						await setSubmitting(false)
					}

					resolve()
				} catch (error) {
					captureException('Signin', error)
					setErrors({ password: error.message })
					reject(error.message)
				}
			})
		})
	}

	@action
	setRequireCompleteProfile = (require) => {
		this.requireCompleteProfile = require
	}

	@action
	setWorker = (worker) => {
		this.worker = worker
	}
}

export const authStore = new AuthStoreCandidate()
